import cloneDeep from 'lodash/cloneDeep';
import isNumber from 'lodash/isNumber';
import type { SearchResultHit, Item, LoadedItem } from '@mimir/common/dto/searchResultHit';
import { isDeleting } from '@mimir/common/dto/itemModels';
import { ItemState } from '@mimir/common/dto/itemState';
import { ItemType } from '@mimir/common/dto/itemTypes';
import type { Commit, Module } from 'vuex';
import type { State } from './state';
import { AsyncOperationState } from '@mimir/common/dto/asyncOperationPrimitives';
import type Relations from '@mimir/common/dto/relationModels';
import type { RelatedItem } from '@mimir/common/dto/relationModels';
import type { DragObject, ThumbnailClick, ThumbnailStripClick } from '@mimir/web/pages/search/components/search-page-interfaces';
import { logErrorAndShowToast } from '@mimir/web/util/uiError';
import Vue from 'vue';
import { handleMdfUpdatesToItem } from '@mimir/common/mdf/update';
import { OperationApiContext } from '@mimir/common/util/operationApiContext';
import { globalBackendClient } from '@mimir/client-common/globals/backendClient';
import type { VueDeleteValue, VueSetValue } from '@mimir/common/util/general';
import { ExponentialBackoff } from '@mimir/common/async/resilience';
import type { SearchPlayerModuleState, UpdateCurrentPreviewItemPayload } from './search-player-module-state';
import type { MetadataSnapshot } from '@mimir/common/dto/mdfModels';
import type { QuickPreviewTabName } from '@mimir/common/types/UIOnlySections';
import { ApiActions } from '@mimir/client-common/util/apiActions';
import { getGlobalStore } from '.';
import type { ItemMediaPlayerOps, PostLoadSeek } from '@mimir/web/model/itemMediaPlayerOps';
import type { TenantConfigDto } from '@mimir/common/dto/tenantModel';
import { getMediaDuration } from '@mimir/common/util/item';

function makeSearchPlayerModuleState(): SearchPlayerModuleState {
  return {
    currentItem: null,
    selectedRelatedItem: null,
    relations: [],
    selectedTab: 'metadata',
    previewCollapsed: false,
    dragObject: null,
    recentlyPreviewed: [],
    dragWithCtrlOrAlt: false,
    isItemMetadataLoadingMap: {},
    mediaPreviewCollapsed: false,
  };
}

export const searchPlayerModule: Module<SearchPlayerModuleState, State> = {

  state: makeSearchPlayerModuleState(),

  mutations: {
    setRelations: (state, payload: Relations) => state.relations = payload,
    setRelatedItemHasBeenUploadedToInRelations: (state, payload: { id: string, hasBeenUploadedTo: boolean}) => {
      const relatedItem = state.relations.find(r => r.item.id === payload.id);
      if (relatedItem) {
        Vue.set(relatedItem.item, 'hasBeenUploadedTo', payload.hasBeenUploadedTo);
      }
    },
    setIsItemMetadataLoading: (state, payload: { id: string, loading: boolean }) => Vue.set(state.isItemMetadataLoadingMap, payload.id, payload.loading),
    setSelectedQuickPreviewTab: (state, payload: QuickPreviewTabName) => state.selectedTab = payload,
    setDragObject: (state, payload: DragObject | null) => state.dragObject = payload,
    setSearchItem: (state, payload: { item: Item | null, quickPreviewMediaPlayerOps: ItemMediaPlayerOps }) => {
      const { item, quickPreviewMediaPlayerOps } = payload;
      if (item && item.id === state.currentItem?.id) {
        /**
         * TODO: If we already have this item as the currentItem, we should
         * probably updating the existing item instead of overwriting it, since
         * we mutate the currentItem with client state from various places.
         */
        console.warn('setSearchItem: item is already the current item');
      }
      state.currentItem = cloneDeep(item);
      if (state.currentItem === null) {
        quickPreviewMediaPlayerOps.clear();
      } else {
        addToRecentlyPreviewed(state.recentlyPreviewed, state.currentItem);
      }
    },
    setRelatedItem: (state, payload: RelatedItem | null) => {
      state.selectedRelatedItem = payload;
      if (state.selectedRelatedItem !== null) {
        addToRecentlyPreviewed(state.recentlyPreviewed, state.selectedRelatedItem);
      }
    },
    setCollapsed: (state, payload: boolean) => state.previewCollapsed = payload,
    setDragWithCtrlOrAlt: (state, payload: boolean) => state.dragWithCtrlOrAlt = payload,
    updateCurrentPreviewItem: (state, payload: UpdateCurrentPreviewItemPayload) => {
      if (!state.currentItem || payload.id !== state.currentItem.id) return;
      const keysToIterateOver = Object.keys(payload.update) as (keyof LoadedItem)[];
      const vueSet = (v: VueSetValue) => Vue.set(v.object, v.key, v.value);
      const vueDeleter = (v: VueDeleteValue) => Vue.delete(v.object, v.key);
      for (const prop of keysToIterateOver) {
        if (prop === 'metadata' && payload.update.metadata !== undefined) {
          let metadataVersion = payload.update.metadataVersion;
          if (metadataVersion == undefined) {
            console.warn('updateCurrentPreviewItem: metadata update without metadataVersion');
            metadataVersion = 0;
          }
          handleMdfUpdatesToItem(state.currentItem, payload.update.metadata, metadataVersion, vueSet, vueDeleter);
        } else if (payload.update[prop] === undefined) {
          Vue.delete(state.currentItem, prop);
        } else {
          Vue.set(state.currentItem, prop, payload.update[prop]);
        }
      }
    },
    setSearchPlayerItemMetadataSnapshot: (state, data: MetadataSnapshot | undefined) => {
      if (state.currentItem)
        Vue.set(state.currentItem, 'metadataSnapshot', data);
    },
    setMediaPreviewCollapsed: (state, payload: boolean) => state.mediaPreviewCollapsed = payload,
  },
  getters: {
    recentlyPreviewed: state => state.recentlyPreviewed,
    selectedQuickPreviewTab: state => state.selectedTab,
    isDraggingObject: state => state.dragObject != null,
    dragObject: state => state.dragObject,
    currentItem: state => state.currentItem,
    previewCollapsed: state => state.previewCollapsed,
    currentItemId: state => state.currentItem?.id,
    selectedRelatedItem: state => state.selectedRelatedItem,
    dragWithCtrlOrAlt: state => state.dragWithCtrlOrAlt,
    isItemMetadataLoadingMap: state => state.isItemMetadataLoadingMap,
    isMediaPreviewCollapsed: state => state.mediaPreviewCollapsed,
  },
  actions: {
    updateQuickPreviewReference: async ({ state: { currentItem, isItemMetadataLoadingMap, recentlyPreviewed }, commit, dispatch }, hits: SearchResultHit[]) => {
      if (currentItem === null) return;
      const match = hits.find(hit => hit.id === currentItem.id);
      if (match) {
        // We need to make sure the currentItem has updated metaData.
        if (!isItemMetadataLoadingMap[currentItem.id]) {
          try {
            await dispatch('loadCurrentItemMetadata');
          } catch (err) {
            console.error(`Error fetching currentItem metadata ${err}`);
          }
        }
        // If we found a match, we should also mark it as selected.
        commit('addToSelectedSearchItems', match);
        addToRecentlyPreviewed(recentlyPreviewed, currentItem);
      }
    },
    emitPlayerError: ({ state }, ev: ErrorEvent) => {
      // Suppress error for now if its a placeholder. This should be removed once we have a quick preview
      // component for place holders (likely a drag and drop component. Issue #426)
      const item = state.currentItem;
      if (item !== null &&
          item.itemType === ItemType.VIDEO &&
          item.itemState !== ItemState.NEW &&
          item.itemState !== ItemState.HIGHRES_AVAILABLE &&
          !isDeleting(item) &&
          item.transcodingState &&
          item.transcodingState !== AsyncOperationState.PROGRESSING &&
          item.transcodingState !== AsyncOperationState.INITIATED) {
        logErrorAndShowToast('Failed to load video', ev);
      }
    },
    setCollapsed: (context, payload: { shouldBeCollapsed: boolean, quickPreviewMediaPlayerOps: ItemMediaPlayerOps }) => {
      const { shouldBeCollapsed, quickPreviewMediaPlayerOps } = payload;
      if (shouldBeCollapsed) {
        quickPreviewMediaPlayerOps.pause();
      }
      context.commit('setCollapsed', shouldBeCollapsed);
    },
    /*
      TODO: This action may need to be rethought entirely, since it manipulates
      non-plain objects stored in the Vuex store, in violation of the Vuex API
      contract.
    */
    loadItemInQuickPreview: (context, payload: {
      item: Pick<Item, 'id' | 'itemType'> | null,
      fromHistory?: true,
      autoPlayFrom?: number,
      quickPreviewMediaPlayerOps: ItemMediaPlayerOps,
      timeToSeekInMs?: number,
    }) => {
      const { item, fromHistory, autoPlayFrom, quickPreviewMediaPlayerOps, timeToSeekInMs } = payload;
      if (item !== null && quickPreviewMediaPlayerOps.getInitializedObs().currentValue() && (item.itemType === ItemType.VIDEO || item.itemType === ItemType.AUDIO)) {
        const apiContext = new OperationApiContext(globalBackendClient, new ExponentialBackoff());
        const apiActions = new ApiActions(apiContext);
        const autoplay = isNumber(autoPlayFrom) ? true : !quickPreviewMediaPlayerOps.getPausedObs().currentValue();
        const useNewBufferingLogic = (context.rootGetters.featureFlags as TenantConfigDto['featureFlags'])?.useNewBufferingLogic ?? false;
        const flags = {
          useFlacDownmix: true,
          useNewBufferingLogic,
        };
        const seekPositionInMs = (isNumber(autoPlayFrom) ? autoPlayFrom : (timeToSeekInMs ?? 0));
        const postLoadOperation: PostLoadSeek = {
          type: 'seek',
          positionInMs: seekPositionInMs,
          play: autoplay,
        };
        quickPreviewMediaPlayerOps.setItemId(apiActions, item.id, flags, { postLoadOperation });
      }
      context.commit('setRelatedItem', null);
      if (fromHistory) {
        // When navigating via history, to reduce user confusion we clear selection in search, because there's no guarantee that the item
        // that will be previewed will be visible in the current search result.
        context.commit('clearSelectedSearchItems');
      }
      context.commit('setSearchItem', { item, quickPreviewMediaPlayerOps });
      return;
    },
    loadRelatedItemInQuickPreview: (context, payload: { event: ThumbnailStripClick | ThumbnailClick, quickPreviewMediaPlayerOps: ItemMediaPlayerOps }) => {
      const { event, quickPreviewMediaPlayerOps } = payload;
      const wasPlaying = !quickPreviewMediaPlayerOps.getPausedObs().currentValue();
      if (event.item !== context.state.selectedRelatedItem) {
        context.commit('setRelatedItem', event.item);
        if (event.item.itemType === ItemType.VIDEO || event.item.itemType === ItemType.AUDIO) {
          const apiContext = new OperationApiContext(globalBackendClient, new ExponentialBackoff());
          const apiActions = new ApiActions(apiContext);
          const useNewBufferingLogic = (context.rootGetters.featureFlags as TenantConfigDto['featureFlags'])?.useNewBufferingLogic ?? false;
          const flags = {
            useFlacDownmix: true,
            useNewBufferingLogic,
          };

          quickPreviewMediaPlayerOps.setItemId(apiActions, event.item.id, flags);
          context.commit('setCollapsed', false);
        } else {
          quickPreviewMediaPlayerOps.clear();
        }
      }

      const mediaDuration = getMediaDuration(event.item);
      if (mediaDuration)  {
        let time = 0;
        if (isThumbnailStripClick(event) && mediaDuration) {
          time = mediaDuration * event.percentageOfVideo;
        }
        setTimeout(() => {
          if (wasPlaying) {
            quickPreviewMediaPlayerOps.play(time);
          } else {
            quickPreviewMediaPlayerOps.setTime(time);
          }
        }, 100);
      }
    },
    unloadSearchPlayer: (context, quickPreviewMediaPlayerOps: ItemMediaPlayerOps) => {
      quickPreviewMediaPlayerOps.clear();
    },
  },
};

/**
 * If previewable, track recently previewed items. If an item is already in the list, to avoid duplicates, we remove the old copy
 * and add a new clone to the top of the list. Items may have changed in the meantime.
 *
 */
const addToRecentlyPreviewed = (recentlyPreviewedList: (Item | RelatedItem)[], itemToAdd: Item | RelatedItem) => {

  if (!isPreviewable(itemToAdd)) {
    return;
  }

  const indexOfExisting = recentlyPreviewedList.findIndex(item => item.id === itemToAdd.id);
  if (indexOfExisting >= 0) {
    recentlyPreviewedList.splice(indexOfExisting, 1);
  }
  recentlyPreviewedList.unshift(cloneDeep(itemToAdd));
};

const isPreviewable = (item: Item | RelatedItem): boolean => {
  return item.itemType !== ItemType.FOLDER;
};

const isThumbnailStripClick = (event: ThumbnailStripClick | ThumbnailClick): event is ThumbnailStripClick => {
  return 'percentageOfVideo' in event;
};

export const commitUpdateCurrentPreviewItem = (commit: Commit, payload: UpdateCurrentPreviewItemPayload) => {
  commit('updateCurrentPreviewItem', payload);
};

export async function loadItemInQuickPreview(payload: {
  item: Pick<Item, 'id' | 'itemType'> | null,
  fromHistory?: true,
  autoPlayFrom?: number,
  quickPreviewMediaPlayerOps: ItemMediaPlayerOps,
  timeToSeekInMs?: number,
}): Promise<void> {
  await getGlobalStore().dispatch('loadItemInQuickPreview', payload);
}
